import { useTranslation } from 'react-i18next';
import SEOHead from '@/components/feature/SEOHead';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { useState, useRef, type FormEvent } from 'react';

const CONTACT_FORM_URL = 'https://readdy.ai/api/form/d9u4h953pcjqs2fcsm20';

export default function About() {
  const { t } = useTranslation();

  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formErrorMsg, setFormErrorMsg] = useState('');
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const formRef = useRef<HTMLFormElement>(null);

  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于我们——般若智慧修行平台',
    description: '般若智慧是以《心经》「色即是空」为核心的佛教哲学修行内容平台，致力于用通俗易懂的方式传播空性智慧，帮助现代人在生活中践行佛法。',
    url: 'https://dharmaway.github.io/about',
  };

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '般若智慧',
    url: 'https://dharmaway.github.io',
    description: '以《心经》「色即是空」为核心的佛教哲学修行平台。',
    email: 'contact@prajna-wisdom.org',
    telephone: '+86 400-888-9999',
    address: {
      '@type': 'PostalAddress',
      streetAddress: '灵隐路18号',
      addressLocality: '杭州',
      addressRegion: '浙江',
      postalCode: '310000',
      addressCountry: 'CN',
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: t('breadcrumb_home'),
        item: 'https://dharmaway.github.io',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: t('breadcrumb_about'),
      },
    ],
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.email.trim() || !formData.message.trim()) return;

    const form = e.currentTarget;
    const fd = new FormData(form);
    const honeypotVal = (fd.get('company_alt') as string || '').trim();
    if (honeypotVal) {
      setFormStatus('success');
      setFormData({ name: '', email: '', message: '' });
      return;
    }

    try {
      const res = await fetch(CONTACT_FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({
          name: formData.name.trim(),
          email: formData.email.trim(),
          message: formData.message.trim(),
        }).toString(),
      });
      const responseText = await res.text();
      let parsed: any = {};
      try { parsed = JSON.parse(responseText); } catch { /* ignore */ }

      if (res.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormData({ name: '', email: '', message: '' });
      } else {
        const msg = parsed?.meta?.message || parsed?.message || responseText || '提交失败';
        setFormStatus('error');
        setFormErrorMsg(msg.toLowerCase().includes('spam') ? '提交失败，请稍后重试。' : msg);
      }
    } catch {
      setFormStatus('error');
      setFormErrorMsg('网络错误，请稍后重试。');
    }
  };

  const milestones = [
    { year: '2020', title: '缘起', desc: '一群佛学爱好者与修行者因共同的愿心聚在一起，决定创建一个面向大众的般若智慧传播平台。' },
    { year: '2021', title: '初建', desc: '平台正式上线，首年发布超过50篇深度解读文章，涵盖心经解读、修行方法、空性哲学等核心领域。' },
    { year: '2022', title: '成长', desc: '读者突破十万，文章被多家佛学媒体转载。启动线下禅修活动，将线上学习与线下实践结合。' },
    { year: '2023', title: '深化', desc: '推出系列课程《般若智慧与现代生活》，邀请多位法师与学者参与内容共创，内容体系日趋完善。' },
    { year: '2024', title: '绽放', desc: '平台用户突破五十万，成为中文世界最具影响力的般若智慧内容平台之一。持续以通俗易懂的方式传播空性智慧。' },
  ];

  const team = [
    {
      name: '释明心',
      role: '主编 / 内容总监',
      desc: '出家二十余年，专研般若中观，致力于将深奥佛学用现代语言传播。',
      image: 'https://readdy.ai/api/search-image?query=Buddhist%20monk%20in%20traditional%20robes%2C%20serene%20expression%2C%20warm%20temple%20interior%20lighting%2C%20spiritual%20portrait%2C%20soft%20warm%20tones%2C%20compassionate%20gaze&width=200&height=200&seq=team-monk&orientation=squarish',
    },
    {
      name: '陈慧明',
      role: '联合创始人 / 运营总监',
      desc: '前互联网大厂产品经理，发心用产品思维让佛法智慧触达更多人。',
      image: 'https://readdy.ai/api/search-image?query=Chinese%20professional%20man%20portrait%2C%20warm%20friendly%20smile%2C%20simple%20studio%20background%2C%20soft%20warm%20lighting%2C%20professional%20headshot&width=200&height=200&seq=team-cofounder&orientation=squarish',
    },
    {
      name: '林静怡',
      role: '修行指导 / 心理咨询师',
      desc: '注册心理咨询师，将佛学正念与现代心理学融合，为读者提供切实可行的修行指导。',
      image: 'https://readdy.ai/api/search-image?query=Chinese%20woman%20professional%20portrait%2C%20warm%20kind%20smile%2C%20soft%20studio%20lighting%2C%20calm%20and%20approachable%2C%20professional%20headshot&width=200&height=200&seq=team-counselor&orientation=squarish',
    },
  ];

  return (
    <>
      <SEOHead
        title="关于我们——般若智慧修行平台 | 色即是空"
        description="般若智慧是以《心经》「色即是空」为核心的佛教哲学修行内容平台，致力于用通俗易懂的方式传播空性智慧，帮助现代人在生活中践行佛法。了解我们的品牌故事、团队与联系方式。"
        keywords="关于我们,般若智慧,色即是空,修行平台,佛学内容,联系"
        canonicalUrl="https://dharmaway.github.io/about"
      />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(aboutSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(orgSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />

      <main className="pt-16 min-h-screen bg-background-50">
        <Breadcrumb
          items={[
            { label: t('breadcrumb_home'), path: '/' },
            { label: t('breadcrumb_about') },
          ]}
        />

        {/* Hero */}
        <section className="relative w-full py-20 md:py-28 px-4 md:px-6 overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=Warm%20abstract%20light%20texture%20background%2C%20soft%20golden%20glow%2C%20minimalist%20elegant%20composition%2C%20flowing%20gentle%20gradients%2C%20serene%20and%20peaceful%20atmosphere%2C%20warm%20cream%20and%20amber%20tones&width=1600&height=600&seq=about-hero&orientation=landscape"
              alt=""
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-background-950/40" />
          </div>
          <div className="relative z-10 max-w-3xl mx-auto text-center">
            <h1 className="text-3xl md:text-5xl font-heading font-bold text-background-50 mb-4">
              关于我们
            </h1>
            <p className="text-base md:text-lg text-background-50/85 leading-relaxed">
              以般若智慧照亮人生，用空性哲学安顿心灵——我们致力于用通俗易懂的方式传播「色即是空」的智慧，帮助现代人在纷繁世界中找到内心的宁静。
            </p>
          </div>
        </section>

        {/* Brand Story */}
        <section className="py-14 md:py-20 px-4 md:px-6 bg-background-50">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3 text-center">
              品牌故事
            </h2>
            <p className="text-foreground-600 text-sm md:text-base text-center mb-10 max-w-2xl mx-auto">
              「般若智慧」诞生于一群修行者共同的愿心——让两千年前的般若经典智慧，走进现代人的生活。
            </p>

            <div className="relative">
              <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-background-200 transform md:-translate-x-px" />
              <div className="space-y-8">
                {milestones.map((item, index) => (
                  <div
                    key={index}
                    className={`relative flex flex-col md:flex-row gap-4 md:gap-8 ${
                      index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                    }`}
                  >
                    <div className={`flex-1 ${index % 2 === 0 ? 'md:text-right' : 'md:text-left'}`}>
                      <div className={`bg-background-50 border border-background-200/70 rounded-lg p-5 ${index % 2 === 0 ? 'md:mr-0' : 'md:ml-0'}`}>
                        <span className="inline-block text-xs font-bold text-accent-500 bg-accent-100 px-2.5 py-1 rounded-full mb-2">
                          {item.year}
                        </span>
                        <h3 className="text-base font-heading font-semibold text-foreground-900 mb-1">
                          {item.title}
                        </h3>
                        <p className="text-sm text-foreground-600 leading-relaxed">
                          {item.desc}
                        </p>
                      </div>
                    </div>
                    <div className="flex-shrink-0 flex items-center justify-center">
                      <div className="w-3 h-3 rounded-full bg-accent-500 z-10" />
                    </div>
                    <div className="flex-1 hidden md:block" />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Team */}
        <section className="py-14 md:py-20 px-4 md:px-6 bg-background-100">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3 text-center">
              核心团队
            </h2>
            <p className="text-foreground-600 text-sm md:text-base text-center mb-10 max-w-2xl mx-auto">
              佛学修行者、前互联网人、心理咨询师——不同背景的他们因共同的愿心走到一起。
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
              {team.map((member, index) => (
                <div
                  key={index}
                  className="bg-background-50 border border-background-200/70 rounded-lg p-6 text-center"
                >
                  <div className="w-20 h-20 mx-auto rounded-full overflow-hidden mb-4">
                    <img
                      src={member.image}
                      alt={member.name}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  </div>
                  <h3 className="text-base font-heading font-semibold text-foreground-900 mb-1">
                    {member.name}
                  </h3>
                  <p className="text-xs font-medium text-primary-600 mb-3">{member.role}</p>
                  <p className="text-sm text-foreground-600 leading-relaxed">{member.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Form */}
        <section className="py-14 md:py-20 px-4 md:px-6 bg-background-50">
          <div className="max-w-lg mx-auto">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3 text-center">
              联系我们
            </h2>
            <p className="text-foreground-600 text-sm md:text-base text-center mb-8">
              有任何建议、合作意向或修行困惑，欢迎与我们分享。
            </p>

            <form ref={formRef} onSubmit={handleSubmit} className="space-y-4" data-readdy-form="">
              <div>
                <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  你的名字
                </label>
                <input
                  id="contact-name"
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={(e) => {
                    setFormData((p) => ({ ...p, name: e.target.value }));
                    if (formStatus !== 'idle') setFormStatus('idle');
                  }}
                  required
                  className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-200/70 text-foreground-900 text-sm focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100"
                  placeholder="请输入你的名字"
                />
              </div>
              <div>
                <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  邮箱地址
                </label>
                <input
                  id="contact-email"
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={(e) => {
                    setFormData((p) => ({ ...p, email: e.target.value }));
                    if (formStatus !== 'idle') setFormStatus('idle');
                  }}
                  required
                  className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-200/70 text-foreground-900 text-sm focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100"
                  placeholder="请输入你的邮箱"
                />
              </div>
              <div>
                <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  留言内容
                </label>
                <textarea
                  id="contact-message"
                  name="message"
                  value={formData.message}
                  onChange={(e) => {
                    setFormData((p) => ({ ...p, message: e.target.value }));
                    if (formStatus !== 'idle') setFormStatus('idle');
                  }}
                  required
                  maxLength={500}
                  rows={4}
                  className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-200/70 text-foreground-900 text-sm focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 resize-none"
                  placeholder="请输入你想说的话（最多500字）"
                />
                <p className="text-xs text-foreground-400 mt-1 text-right">{formData.message.length}/500</p>
              </div>
              <input
                type="text"
                name="company_alt"
                tabIndex={-1}
                autoComplete="off"
                readOnly
                aria-hidden="true"
                className="honeypot-field"
              />
              <button
                type="submit"
                className="w-full py-3 rounded-lg bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                发送留言
              </button>

              {formStatus === 'success' && (
                <p className="text-sm text-green-600 text-center">感谢你的留言，我们会尽快回复！</p>
              )}
              {formStatus === 'error' && (
                <p className="text-sm text-red-500 text-center">{formErrorMsg}</p>
              )}
            </form>

            <div className="mt-10 pt-8 border-t border-background-200/70 grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="w-10 h-10 mx-auto flex items-center justify-center rounded-full bg-primary-100 text-primary-600 mb-3">
                  <i className="ri-mail-line text-lg" />
                </div>
                <p className="text-xs text-foreground-500 mb-1">电子邮箱</p>
                <p className="text-sm text-foreground-800">{t('contact_email')}</p>
              </div>
              <div className="text-center">
                <div className="w-10 h-10 mx-auto flex items-center justify-center rounded-full bg-secondary-100 text-secondary-600 mb-3">
                  <i className="ri-phone-line text-lg" />
                </div>
                <p className="text-xs text-foreground-500 mb-1">联系电话</p>
                <p className="text-sm text-foreground-800">{t('contact_phone')}</p>
              </div>
              <div className="text-center">
                <div className="w-10 h-10 mx-auto flex items-center justify-center rounded-full bg-accent-100 text-accent-600 mb-3">
                  <i className="ri-map-pin-line text-lg" />
                </div>
                <p className="text-xs text-foreground-500 mb-1">地址</p>
                <p className="text-sm text-foreground-800">{t('contact_address')}</p>
              </div>
            </div>
          </div>
        </section>
      </main>
    </>
  );
}